import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Upload, FileText, Download, Trash2, Search, 
  File, FileImage, FileVideo, FileAudio, Filter, X, FolderOpen
} from 'lucide-react';
import { saveAs } from 'file-saver';
import Navigation from '../components/Navigation';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { trackFeatureUsage, trackDocumentAccess } from '../utils/analytics';
import { formatDate } from '../utils/dateFormat';

import { API, API_BASE } from '../config/api';
const BACKEND_URL = API_BASE;

const FILE_ICONS = {
  'application/pdf': FileText,
  'image/': FileImage,
  'video/': FileVideo,
  'audio/': FileAudio,
  'default': File
};

const getFileIcon = (fileType) => {
  if (fileType?.startsWith('image/')) return FileImage;
  if (fileType?.startsWith('video/')) return FileVideo;
  if (fileType?.startsWith('audio/')) return FileAudio;
  if (fileType === 'application/pdf') return FileText;
  return File;
};

const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

function Documents({ user }) {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [documents, setDocuments] = useState([]);
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCase, setFilterCase] = useState('all');
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedCase, setSelectedCase] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);

  const isStaffOrAdmin = user?.role === 'staff' || user?.role === 'admin';

  const handleLogout = async () => {
    await fetch(`${API}/auth/logout`, { method: 'POST', credentials: 'include' });
    window.location.href = '/login';
  };

  useEffect(() => {
    fetchData();
    // Track documents page visit
    trackFeatureUsage('Documents');
  }, []);

  const fetchData = async () => {
    try {
      const [docsRes, casesRes] = await Promise.all([
        fetch(`${API}/documents`, { credentials: 'include' }),
        fetch(`${API}/cases`, { credentials: 'include' })
      ]);

      if (docsRes.ok) setDocuments(await docsRes.json());
      if (casesRes.ok) setCases(await casesRes.json());
    } catch (error) {
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !selectedCase) {
      toast.error('Please select a file and case');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('case_id', selectedCase);

    try {
      const response = await fetch(`${API}/documents/upload`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      if (response.ok) {
        toast.success('Document uploaded successfully');
        setUploadDialogOpen(false);
        setSelectedFile(null);
        setSelectedCase('');
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Upload failed');
      }
    } catch (error) {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (doc) => {
    try {
      // Track document download for analytics
      trackDocumentAccess(doc.document_id, 'download');
      
      // Fetch with credentials to ensure authentication
      const response = await fetch(`${API}/documents/${doc.document_id}/download`, {
        method: 'GET',
        credentials: 'include'
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        // Show clearer message for file not found
        if (response.status === 404) {
          toast.error('This document is no longer available. It may have been removed during a system update. Please re-upload the document.');
          return;
        }
        throw new Error(errorData.detail || 'Failed to download document');
      }
      
      // Get the blob and create download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = doc.file_name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Download complete');
    } catch (error) {
      console.error('Download error:', error);
      toast.error(error.message || 'Failed to download document');
    }
  };

  const handleDelete = async (documentId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;

    try {
      const response = await fetch(`${API}/documents/${documentId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Document deleted');
        fetchData();
      } else {
        toast.error('Failed to delete document');
      }
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  const getCaseName = (caseId) => {
    const caseItem = cases.find(c => c.case_id === caseId);
    return caseItem?.case_name || 'Unknown Case';
  };

  const filteredDocuments = documents.filter(doc => {
    const matchesSearch = doc.file_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         getCaseName(doc.case_id).toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCase = filterCase === 'all' || doc.case_id === filterCase;
    return matchesSearch && matchesCase;
  });

  // Group documents by case
  const documentsByCase = filteredDocuments.reduce((acc, doc) => {
    const caseName = getCaseName(doc.case_id);
    if (!acc[caseName]) {
      acc[caseName] = [];
    }
    acc[caseName].push(doc);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation user={user} onLogout={handleLogout} />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#80298F]"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation user={user} onLogout={handleLogout} />
      
      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 text-gray-600 hover:text-[#80298F] mb-4 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Dashboard
          </button>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-heading font-bold text-gray-900">Documents</h1>
              <p className="text-gray-600 mt-1">
                {isStaffOrAdmin ? 'Manage and share documents with clients' : 'View, download, and upload your case documents'}
              </p>
            </div>
            <Button 
              onClick={() => setUploadDialogOpen(true)}
              className="bg-[#80298F] hover:bg-[#9b3ba8]"
            >
              <Upload className="h-4 w-4 mr-2" />
              Upload Document
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search documents..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="w-full sm:w-64">
              <Select value={filterCase} onValueChange={setFilterCase}>
                <SelectTrigger>
                  <Filter className="h-4 w-4 mr-2 text-gray-400" />
                  <SelectValue placeholder="Filter by case" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Cases</SelectItem>
                  {cases.map(c => (
                    <SelectItem key={c.case_id} value={c.case_id}>
                      {c.case_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Document Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl border border-gray-100 p-4">
            <p className="text-sm text-gray-500">Total Documents</p>
            <p className="text-2xl font-bold text-gray-900">{documents.length}</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-100 p-4">
            <p className="text-sm text-gray-500">Cases</p>
            <p className="text-2xl font-bold text-[#80298F]">{Object.keys(documentsByCase).length}</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-100 p-4">
            <p className="text-sm text-gray-500">PDFs</p>
            <p className="text-2xl font-bold text-red-600">
              {documents.filter(d => d.file_type === 'application/pdf').length}
            </p>
          </div>
          <div className="bg-white rounded-xl border border-gray-100 p-4">
            <p className="text-sm text-gray-500">Images</p>
            <p className="text-2xl font-bold text-blue-600">
              {documents.filter(d => d.file_type?.startsWith('image/')).length}
            </p>
          </div>
        </div>

        {/* Documents List */}
        {filteredDocuments.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-12 text-center">
            <FolderOpen className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Documents Found</h3>
            <p className="text-gray-500 mb-4">
              {searchQuery || filterCase !== 'all' 
                ? 'Try adjusting your search or filter criteria'
                : isStaffOrAdmin 
                  ? 'Upload documents to share with clients'
                  : 'Upload documents for your case or wait for your attorney to share documents with you'}
            </p>
            {!searchQuery && filterCase === 'all' && (
              <Button 
                onClick={() => setUploadDialogOpen(true)}
                className="bg-[#80298F] hover:bg-[#9b3ba8]"
              >
                <Upload className="h-4 w-4 mr-2" />
                Upload First Document
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-6">
            {Object.entries(documentsByCase).map(([caseName, docs]) => (
              <div key={caseName} className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                <div className="bg-gray-50 px-6 py-3 border-b border-gray-100">
                  <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                    <FolderOpen className="h-4 w-4 text-[#FAA519]" />
                    {caseName}
                    <span className="text-sm font-normal text-gray-500">({docs.length} document{docs.length !== 1 ? 's' : ''})</span>
                  </h3>
                </div>
                <div className="divide-y divide-gray-100">
                  {docs.map(doc => {
                    const FileIcon = getFileIcon(doc.file_type);
                    return (
                      <div key={doc.document_id} className="flex items-center justify-between p-4 hover:bg-gray-50 transition-colors">
                        <div className="flex items-center gap-4 flex-1 min-w-0">
                          <div className={`p-2 rounded-lg ${
                            doc.file_type === 'application/pdf' ? 'bg-red-100 text-red-600' :
                            doc.file_type?.startsWith('image/') ? 'bg-blue-100 text-blue-600' :
                            doc.file_type?.startsWith('video/') ? 'bg-purple-100 text-purple-600' :
                            'bg-gray-100 text-gray-600'
                          }`}>
                            <FileIcon className="h-5 w-5" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="font-medium text-gray-900 truncate">{doc.file_name}</h4>
                            <p className="text-sm text-gray-500">
                              {formatFileSize(doc.file_size)} • Uploaded by {doc.uploader_name} • {formatDate(doc.created_at)}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 ml-4">
                          <button
                            onClick={async (e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              
                              try {
                                // Track document download for analytics
                                trackDocumentAccess(doc.document_id, 'download');
                                
                                // Fetch the file with credentials
                                const response = await fetch(`${API}/documents/${doc.document_id}/download`, {
                                  method: 'GET',
                                  credentials: 'include'
                                });
                                
                                if (!response.ok) {
                                  throw new Error('Failed to download document');
                                }
                                
                                // Get the blob and trigger download
                                const blob = await response.blob();
                                saveAs(blob, doc.file_name);
                                
                                toast.success('Download started');
                              } catch (err) {
                                console.error('Error downloading document:', err);
                                toast.error('Failed to download document');
                              }
                            }}
                            className="inline-flex items-center justify-center h-9 px-3 rounded-md border border-[#80298F] text-[#80298F] hover:bg-[#80298F] hover:text-white transition-colors text-sm font-medium gap-1"
                            title={`Download ${doc.file_name}`}
                          >
                            <Download className="h-4 w-4" />
                            <span className="hidden sm:inline">Download</span>
                          </button>
                          {isStaffOrAdmin && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDelete(doc.document_id)}
                              className="text-red-600 border-red-200 hover:bg-red-50"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Upload Dialog */}
      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent className="sm:max-w-md bg-white">
          <DialogHeader>
            <DialogTitle>Upload Document</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Select Case</Label>
              <Select value={selectedCase} onValueChange={setSelectedCase}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose a case" />
                </SelectTrigger>
                <SelectContent>
                  {cases.map(c => (
                    <SelectItem key={c.case_id} value={c.case_id}>
                      {c.case_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Select File</Label>
              <div 
                onClick={() => fileInputRef.current?.click()}
                className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-[#80298F] transition-colors"
              >
                {selectedFile ? (
                  <div className="flex items-center justify-center gap-2">
                    <FileText className="h-5 w-5 text-[#80298F]" />
                    <span className="text-sm text-gray-700">{selectedFile.name}</span>
                    <button 
                      onClick={(e) => { e.stopPropagation(); setSelectedFile(null); }}
                      className="ml-2 text-gray-400 hover:text-red-500"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <>
                    <Upload className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                    <p className="text-sm text-gray-600">Click to select a file</p>
                    <p className="text-xs text-gray-400 mt-1">PDF, DOC, Images, etc.</p>
                  </>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                onChange={handleFileSelect}
                className="hidden"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleUpload} 
              disabled={uploading || !selectedFile || !selectedCase}
              className="bg-[#80298F] hover:bg-[#9b3ba8]"
            >
              {uploading ? 'Uploading...' : 'Upload'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}

export default Documents;
